import { useEffect } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { PlantMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/components/PlantMasterFormFields'
import { useCreatePlantMaster } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useCreatePlantMaster'
import {
  type PlantMasterFormInput,
  PlantMasterFormSchema,
  type PlantMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/schema'
import { resolvePlantMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/serverError'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const PLANT_MASTER_CREATE_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.plantMaster.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.plantMaster.serverError.aborted',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.plantMaster.serverError.invalidArgument',
}

/**
 * 発電所マスタ新規登録モーダル。
 *
 * `useCrudModalForm` + `CrudModalLayout` に集約 (他 7 タブと同 pattern)。
 */
export const CreatePlantMasterModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreatePlantMaster()

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    isDirty,
  } = useCrudModalForm<PlantMasterFormInput, PlantMasterFormValues>({
    schema: PlantMasterFormSchema,
    defaultValues: {
      plantCode: '',
      plantName: '',
      plantNameAbbr: '',
      areaCode: AreaCode.TOKYO,
      // 妥当な初期選択肢が無く必ずユーザーに選ばせるため、 未選択 (undefined) で開始する。
      // UNSPECIFIED(=0) を初期値にすると NativeSelectField の value='0' に対応する option が
      // 無く、 controlled select が placeholder ('') と不整合になる (schema は refine で 0 を弾く)。
      powerType: undefined,
      voltageClass: undefined,
      maxReceivingPowerKw: '',
      businessMasterId: '',
      contractIdNo2: '',
      // AC-05: 優先順位は空欄 (未設定) を初期値にする (旧: 1 固定入力)。
      priority: undefined,
      proRataPriority: undefined,
      validFrom: todayCivilDate(),
      validTo: undefined,
      note: '',
      members: [{ businessMasterId: '', isOwn: false }],
    },
    onSubmit: async (values) => {
      await mutateAsync({
        plantCode: values.plantCode,
        plantName: values.plantName,
        plantNameAbbr: values.plantNameAbbr,
        areaCode: values.areaCode,
        powerType: values.powerType,
        voltageClass: values.voltageClass,
        // 必須 (schema で非空 + 0 以上を検証済み) のため常に Decimal を組む。
        maxReceivingPowerKw: create(DecimalSchema, {
          value: values.maxReceivingPowerKw,
        }),
        businessMasterId: values.businessMasterId,
        contractIdNo2:
          values.contractIdNo2 !== undefined && values.contractIdNo2 !== ''
            ? values.contractIdNo2
            : undefined,
        priority: values.priority,
        proRataPriority: values.proRataPriority,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // member の有効期間は plant の適用期間を継承する (proto PlantMemberInput
        // .valid_from は required。 画面は member 個別の日付入力を持たないため、
        // plant の validFrom / validTo を全 member に適用する)。
        members: values.members.map((m) => ({
          businessMasterId: m.businessMasterId,
          isOwn: m.isOwn,
          validFrom: {
            year: values.validFrom.year,
            month: values.validFrom.month,
            day: values.validFrom.day,
          },
          validTo:
            values.validTo !== undefined
              ? {
                  year: values.validTo.year,
                  month: values.validTo.month,
                  day: values.validTo.day,
                }
              : undefined,
        })),
        // validFrom は schema で必須化済みのため常に送信する。
        validFrom: {
          year: values.validFrom.year,
          month: values.validFrom.month,
          day: values.validFrom.day,
        },
        validTo:
          values.validTo !== undefined
            ? {
                year: values.validTo.year,
                month: values.validTo.month,
                day: values.validTo.day,
              }
            : undefined,
      })
    },
    onClose,
    customErrorKeys: PLANT_MASTER_CREATE_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.plantMaster.serverError.unknown',
    resolveDetailedError: resolvePlantMasterDetailedError,
  })

  // モーダルオープン時に validFrom を実行日に更新（defaultValues の todayCivilDate() は
  // コンポーネントマウント時に固定化されるため、日跨ぎで開き直しても前日のままになる）。
  useEffect(() => {
    if (isOpen) {
      setValue('validFrom', todayCivilDate())
    }
  }, [isOpen, setValue])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #plant-edit-modal) は `.ecl-modal--lg`。
      size="lg"
      title={t('page:adminOcctoMaster.plantMaster.modal.createTitle')}
    >
      <PlantMasterFormFields
        control={control}
        errors={errors}
        isEdit={false}
        setValue={setValue}
      />
    </CrudModalLayout>
  )
}
